// The ANYTIME FITNESS provider's pure request rules (brief 2026-10-05,
// Addendum 3 — the authoritative payload and field mapping). No I/O. The
// response half is server/modules/leadRelay/anytimefitness.ts.
//
// ⚠️⚠️ ONE VALUE, TWO DESTINATIONS — the defect class this file exists to
// prevent. Three places in this payload carry the same fact twice:
//   · location_number + prospectData.lead_properties.location_id  ← the club id
//   · mobile_phone + home_phone                                    ← the phone
// Each is written from ONE value, computed once. Two config fields (or two
// derivations) that can disagree is a misroute that passes every validation.
// The route config therefore has exactly one club-id field — a type guard in
// anytimefitness.test.ts fails to COMPILE if a second one is added.
// And address_data is OMITTED when there is no postcode — never an empty object.
//
// ⚠️ The club id is the new `lid`: it alone decides which club receives a lead,
// a wrong one returns 201, and nothing here can detect it. The first live lead
// on every new club is confirmed with that club, by a person.
//
// ✅ WIRED (2026-10-07): in LeadSentFields, composed by composeLead, sent by
// server/modules/leadRelay/anytimefitnessAttempt.ts, and PROVIDER_CAN_SEND is
// true. An AF route is still created inactive and in test mode.

import type { AnytimeFitnessRouteConfig, LeadInput, LeadTestDifference } from '../../types/leadRelay.js';
import { type LeadLocale, LEAD_LOCALES, toE164 } from './locale.js';
import { outboundName } from './names.js';
import { type AfOriginSource, afOriginSourceFor } from './platform.js';

export const AF_LEAD_CAPTURE_URL = 'https://react.anytimefitness.com/api/lead-capture/';

/** Per-country request values (Addendum 3). Per-locale from day one, AU and NZ. */
export const AF_COUNTRY_VALUES = {
  AU: {
    locale: 'en-AU',
    websiteUrl: 'https://www.anytimefitness.com/en-au',
    /** The club id prefix AF uses for this country (observed AU-1432). */
    clubId: /^AU-\d+$/,
    /** The club id shown as the route form's hint. */
    clubIdExample: 'AU-1432',
  },
  // ⚠️ ASSUMPTION (NZ, 2026-10-27): AF's lead capture is ONE global endpoint
  // (AF_LEAD_CAPTURE_URL below); NZ differs only by locale, website and club-id
  // prefix. UNVERIFIED LIVE — the first NZ test lead confirms it.
  NZ: {
    locale: 'en-NZ',
    websiteUrl: 'https://www.anytimefitness.com/en-nz',
    clubId: /^NZ-\d+$/,
    clubIdExample: 'NZ-1059',
  },
} as const satisfies Record<
  LeadLocale,
  { locale: string; websiteUrl: string; clubId: RegExp; clubIdExample: string }
>;

/**
 * An Anytime Fitness route's provider config — defined with the other route
 * variants in src/types/leadRelay.ts (step 3 joined it to LeadRouteConfig) and
 * re-exported here, where its one-club-id-field guard lives.
 */
export type { AnytimeFitnessRouteConfig };

export const AF_OFFER_NAME = 'membership inquiry';
export const AF_WORKFLOW_NAME = 'membership-inquiry';
export const AF_NOTE = 'Meta lead via Kyck';
export const AF_USER_ORIGIN = 'web';

/** The request body, key for key as Addendum 3 gives it. Also what `sent` will hold. */
export interface AnytimeFitnessLeadCapture {
  locale: string;
  location_number: string;
  offer_name: typeof AF_OFFER_NAME;
  workflow_name: typeof AF_WORKFLOW_NAME;
  /**
   * ⚠️ `true` on a LIVE lead: AF emails the prospect (with a booking link), and
   * the response sets `sms_opt_out: false` — see the consent flag in Addendum 1.
   * ⚠️ `false` on a TEST lead, and the test record says so (testDifferences):
   * the test proves AF accepts and creates the prospect, and a confirmation
   * email to a person who does not exist can start a club's follow-up workflow.
   */
  send_confirmation_emails: boolean;
  website_url: string;
  prospectData: {
    first_name: string;
    last_name: string;
    email: string;
    mobile_phone: string;
    home_phone: string;
    /** ⚠️ ABSENT when there is no usable postcode — never `{}` and never `{ zip: '' }`. */
    address_data?: { zip: string };
    note: typeof AF_NOTE;
    /**
     * ⚠️ REQUIRED, and FREE TEXT (Addendum 4, five probes against AU-1432):
     * omitted → 400; `TESTSUB` → 201, echoed verbatim. AF validates nothing, so
     * the value always comes from platform.ts's one table (afOriginSourceFor).
     */
    origin_source: AfOriginSource;
    user_origin: typeof AF_USER_ORIGIN;
    lead_properties: { location_id: string };
  };
}

/**
 * What a test lead sends differently from production, and why. ONE list, read
 * by both the payload and the record, so the two cannot disagree.
 */
export const AF_TEST_DIFFERENCES: readonly LeadTestDifference[] = [
  {
    field: 'send_confirmation_emails',
    test: 'false',
    production: 'true',
    why: 'a confirmation email to a test person who does not exist can start the club follow-up workflow',
  },
];

export interface AnytimeFitnessComposeOptions {
  /** A Send-test-lead compose: differs from production in AF_TEST_DIFFERENCES, and says so. */
  kind?: 'live' | 'test';
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The JSON body actually POSTed: the composed (stored) payload with an EMPTY
 * name replaced by NAME_PLACEHOLDER (names.ts). ⚠️ The transport edge is the
 * only place the placeholder exists — `sent` keeps the name empty.
 */
export function leadCaptureRequestBody(sent: AnytimeFitnessLeadCapture): AnytimeFitnessLeadCapture {
  return {
    ...sent,
    prospectData: {
      ...sent.prospectData,
      first_name: outboundName(sent.prospectData.first_name),
      last_name: outboundName(sent.prospectData.last_name),
    },
  };
}

export function anytimeFitnessRouteProblems(config: AnytimeFitnessRouteConfig): Array<{ field: string; message: string }> {
  const values = AF_COUNTRY_VALUES[config.country];
  if (!values) return [{ field: 'country', message: `unknown country ${JSON.stringify(config.country)}` }];
  return values.clubId.test(config.clubId.trim())
    ? []
    : [{ field: 'clubId', message: `must be the Anytime Fitness club id for ${config.country}, e.g. ${values.clubIdExample}` }];
}

export type AnytimeFitnessComposeResult =
  | { ok: true; sent: AnytimeFitnessLeadCapture; testDifferences: LeadTestDifference[] }
  | {
      ok: false;
      reason: 'missing_required';
      missing: string[];
      /** Still returned, so a person can re-key what we do have. */
      sent: AnytimeFitnessLeadCapture;
      testDifferences: LeadTestDifference[];
    };

/**
 * Compose the lead-capture body. Required: first name, last name, a valid
 * email, a phone that normalises to E.164, a valid club id. The postcode is
 * optional — a lead without one returns 201 — and an INVALID one is left out
 * rather than sent (the raw value stays on the record's `input`); it never
 * blocks a lead. There is no interest or model concept: offer and workflow
 * are fixed.
 */
export function composeAnytimeFitnessLead(
  config: AnytimeFitnessRouteConfig,
  input: LeadInput,
  options: AnytimeFitnessComposeOptions = {},
): AnytimeFitnessComposeResult {
  const isTest = options.kind === 'test';
  const values = AF_COUNTRY_VALUES[config.country];
  const rules = LEAD_LOCALES[config.country];
  const missing: string[] = [];

  // ⚠️ ONE club id, read once, written to BOTH location fields.
  const clubId = config.clubId.trim();
  if (!values.clubId.test(clubId)) missing.push('clubId');

  // ⚠️ Names are NOT required here: composeLead resolves them first (names.ts —
  // the ONE split, no provider has its own), and an empty one goes out as
  // NAME_PLACEHOLDER at the transport. Stored empty, exactly as the form gave it.
  const name = { first: (input.first_name ?? '').trim(), last: (input.last_name ?? '').trim() };

  const email = (input.email ?? '').trim();
  if (!EMAIL.test(email)) missing.push('email');

  // ⚠️ ONE normalised phone, computed once, written to BOTH phone fields.
  const phone = toE164(input.phone, config.country);
  if (phone === null) missing.push('phone');

  const postcode = (input.postcode ?? '').trim();
  const zip = rules.postcode.test(postcode) ? postcode : null;

  const sent: AnytimeFitnessLeadCapture = {
    locale: values.locale,
    location_number: clubId,
    offer_name: AF_OFFER_NAME,
    workflow_name: AF_WORKFLOW_NAME,
    // ⚠️ The ONLY test/production difference — and it is recorded below.
    send_confirmation_emails: !isTest,
    website_url: values.websiteUrl,
    prospectData: {
      first_name: name.first,
      last_name: name.last,
      email,
      mobile_phone: phone ?? '',
      home_phone: phone ?? '',
      ...(zip === null ? {} : { address_data: { zip } }),
      note: AF_NOTE,
      // ⚠️ From the ONE platform table (platform.ts) — never a literal here.
      origin_source: afOriginSourceFor(input.platform),
      user_origin: AF_USER_ORIGIN,
      lead_properties: { location_id: clubId },
    },
  };
  const testDifferences = isTest ? AF_TEST_DIFFERENCES.map((d) => ({ ...d })) : [];
  return missing.length > 0
    ? { ok: false, reason: 'missing_required', missing, sent, testDifferences }
    : { ok: true, sent, testDifferences };
}
